import type { OrderDto } from '../../../../dto/order';
import { getOrderProductDisplayName } from '../../orderProductDisplay';
import type { OrderDraftLine, OrderDraftState } from '../draft/orderDraftModel';
import type { OrderGuideDraftFacts, OrderGuideLineFacts } from './orderGuideRules';

/** One line of the checklist from anything that has a name, a quantity and a sale price. */
export const toOrderGuideLine = (line: { name?: string | null; quantity?: number | string | null; unitPrice?: number | string | null }): OrderGuideLineFacts => ({
  name: getOrderProductDisplayName(line as never, line.name || ''),
  hasQuantity: Number(line.quantity) > 0,
  hasPrice: Number(line.unitPrice) > 0,
});

/** Facts of the Szkic checklist from the editor's draft (the new card and the editable saved draft). */
export function getOrderGuideDraftFactsFromDraft(
  draft: OrderDraftState,
  options: { canViewSalePrices: boolean; canOpenPrices: boolean },
): OrderGuideDraftFacts {
  return {
    customerChosen: Boolean(draft.header.customerId),
    lines: draft.lines.map((line: OrderDraftLine) => toOrderGuideLine(line)),
    canViewSalePrices: options.canViewSalePrices,
    deliveryDateSet: Boolean(draft.header.expectedDelivery && draft.header.expectedDelivery.isValid()),
    canOpenPrices: options.canOpenPrices,
  };
}

/** Facts of the Szkic / Oferta checklists from the saved order (a step shown without the editor). */
export function getOrderGuideDraftFactsFromOrder(
  order: Pick<OrderDto, 'customerId' | 'productsLoaded' | 'products' | 'expectedDelivery'>,
  options: { canViewSalePrices: boolean },
): OrderGuideDraftFacts {
  return {
    customerChosen: Boolean(order.customerId),
    lines: (order.productsLoaded ? order.products : []).map((line) => toOrderGuideLine(line)),
    canViewSalePrices: options.canViewSalePrices,
    deliveryDateSet: Boolean(order.expectedDelivery),
    canOpenPrices: false,
  };
}

/** The data of the order is complete enough to offer it: lines with quantities and — for readers of prices — prices. */
export const isOrderGuideDataComplete = (facts: OrderGuideDraftFacts): boolean => facts.lines.length > 0
  && facts.lines.every((line) => line.hasQuantity && (!facts.canViewSalePrices || line.hasPrice));
